/**
 * «Χρώμα» κατηγορίας / προϊόντος στις φόρμες καταλόγου (απόφαση ιδιοκτήτη 12/10/2026, migration 264).
 *
 * Όχι ελεύθερο χρώμα: οι 17 τόνοι της παλέτας του ταμείου (utils/catalogColors) ως δείγματα 44px,
 * με το ελληνικό όνομα στο title/aria-label, και μία κενή επιλογή (= null):
 *   - κατηγορία: «Αυτόματο · <τόνος>» — ο τόνος που δίνει σήμερα ο κανόνας από το όνομα·
 *   - προϊόν: «Χωρίς» — η κάρτα μένει όπως ήταν.
 * Τα δείγματα παίρνουν το χρώμα τους από το CashierV2.css (§4 πίνακας τόνων, §6 .catalog-tone), ώστε
 * να είναι ΑΚΡΙΒΩΣ οι τόνοι του ταμείου σε ανοιχτό και σκούρο.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { CATALOG_TONES, CatalogTone, catalogTone } from '../../utils/catalogColors';

interface CatalogColorPickerProps {
  /** Ο αποθηκευμένος τόνος· null/άγνωστο = αυτόματο (κατηγορία) / χωρίς (προϊόν). */
  value: string | null | undefined;
  onChange: (tone: CatalogTone | null) => void;
  kind: 'category' | 'product';
  /** Κατηγορία: ο τόνος του αυτόματου κανόνα (catalogHelpers.autoCategoryTone), για το «Αυτόματο · …». */
  autoTone?: CatalogTone | null;
  /** Πρόθεμα για τα ids (label ↔ radiogroup) — δύο φόρμες στην ίδια σελίδα δεν συγκρούονται. */
  idPrefix?: string;
  /** Μικρότερη ετικέτα (πλαϊνή φόρμα κεντρικού καταλόγου). */
  compactLabel?: boolean;
}

/** Μικρή κουκκίδα με τον τόνο — για τις λίστες της διαχείρισης («τι έχει οριστεί»). */
export const CatalogToneDot: React.FC<{ tone: string | null | undefined; className?: string }> = ({ tone, className = '' }) => {
  const { t } = useTranslation('products');
  const valid = catalogTone(tone);
  if (!valid) return null;
  const name = t(`products:catalogColor.tones.${valid}`);
  return (
    <span
      role="img"
      aria-label={t('products:catalogColor.listTitle', { tone: name })}
      title={t('products:catalogColor.listTitle', { tone: name })}
      data-tone={valid}
      data-testid="catalog-tone-dot"
      className={`catalog-tone inline-block w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-black/10 dark:ring-white/20 ${className}`}
    />
  );
};

const CatalogColorPicker: React.FC<CatalogColorPickerProps> = ({
  value, onChange, kind, autoTone = null, idPrefix = `catalog-color-${kind}`, compactLabel = false,
}) => {
  const { t } = useTranslation('products');
  const current = catalogTone(value);
  const toneName = (tone: CatalogTone) => t(`products:catalogColor.tones.${tone}`);
  const emptyLabel = kind === 'category'
    ? (autoTone ? t('products:catalogColor.autoWithTone', { tone: toneName(autoTone) }) : t('products:catalogColor.auto'))
    : t('products:catalogColor.none');
  const labelId = `${idPrefix}-label`;

  return (
    <div>
      <span
        id={labelId}
        className={compactLabel ? 'block text-xs font-medium text-t3 mb-1' : 'block text-sm font-medium text-t2 mb-1.5'}
      >
        {t('products:catalogColor.label')}
      </span>
      <div role="radiogroup" aria-labelledby={labelId} className="flex flex-wrap gap-1.5">
        <button
          type="button"
          role="radio"
          aria-checked={current === null}
          onClick={() => onChange(null)}
          className={`h-11 px-3 rounded-xl border text-sm font-medium flex items-center gap-2 transition-colors active:scale-[0.98] touch-manipulation ${
            current === null
              ? 'border-t1 bg-surface-3 text-t1'
              : 'border-line-strong bg-surface-2 text-t2 hover:border-t3'
          }`}
        >
          {kind === 'category' && autoTone ? (
            <span aria-hidden="true" data-tone={autoTone} className="catalog-tone w-4 h-4 rounded-full opacity-70" />
          ) : (
            <span aria-hidden="true" className="w-4 h-4 rounded-full border-2 border-dashed border-line-strong" />
          )}
          <span className="whitespace-nowrap">{emptyLabel}</span>
        </button>
        {CATALOG_TONES.map(tone => {
          const selected = current === tone;
          return (
            <button
              key={tone}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={toneName(tone)}
              title={toneName(tone)}
              data-tone={tone}
              onClick={() => onChange(tone)}
              className={`catalog-tone w-11 h-11 rounded-xl flex items-center justify-center transition-transform active:scale-95 touch-manipulation ${
                selected ? 'ring-2 ring-t1 ring-offset-2 ring-offset-surface-1' : ''
              }`}
            >
              {selected && <Check aria-hidden="true" className="h-5 w-5 text-white drop-shadow" />}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-t4 mt-1.5">
        {kind === 'category' ? t('products:catalogColor.categoryHint') : t('products:catalogColor.productHint')}
      </p>
    </div>
  );
};

export default CatalogColorPicker;
